/**
 * Mixpanel browser SDK helpers for Horizon.
 *
 * - Autocapture and explicit `page_view` work once `initMixpanel` runs with a token.
 * - Session Replay is separate: Mixpanel disables it unless `record_sessions_percent` > 0
 *   at init (default in the SDK is 0). Replays typically appear in the UI within ~1 minute
 *   after capture once enabled.
 * @see https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript/javascript-replay
 */
import mixpanel from "mixpanel-browser";
import type { Persistence } from "mixpanel-browser";

import { mixpanelEmailEventProperties, mixpanelEmailPeopleProperties } from "./mixpanelEmailProps";
import { shouldExcludeInternalEmailFromMixpanel } from "./mixpanelInternalExclude";

type Primitive = string | number | boolean | null | undefined;

type TrackProperties = Record<string, Primitive>;

export interface MixpanelInitOptions {
  /** 0–100. Percentage of SDK initializations that record Session Replay. 0 = no replay (SDK default). */
  recordSessionsPercent?: number;
  /**
   * Browser persistence backend. Chrome extension **content scripts** should use `"memory"` so state
   * is not written to the host page’s storage (PSA origin). Popup/extension pages may use `localStorage`.
   */
  persistence?: "localStorage" | "cookie" | "localStorage+cookie" | "memory";
  /** Applied with mixpanel.register() so every event includes these properties (e.g. product surface). */
  superProperties?: TrackProperties;
  /** When false, disables DOM autocapture (useful for noisy third-party host pages in extensions). */
  autocapture?: boolean;
  /**
   * When true (from INI / REACT_APP_MIXPANEL_EXCLUDE_INTERNAL_EMAILS / extension Vite env),
   * internal email patterns (mixpanelInternalExclude.ts) do not send Mixpanel events.
   */
  excludeInternalEmails?: boolean;
}

let initialized = false;
/** From init: whether internal-email exclusion is enabled in config. */
let excludeInternalEmailsEnabled = false;
/** Last Auth0 email we applied the internal-email policy to (`undefined` = anonymous / not yet synced). */
let mixpanelPolicyEmail: string | undefined;
/** Email from the last `identifyMixpanelUser` call — attached to subsequent track events. */
let identifiedUserEmail: string | undefined;

function clampRecordSessionsPercent(value: unknown): number {
  const n = typeof value === "number" ? value : Number.parseInt(String(value ?? "").trim(), 10);
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, Math.round(n)));
}

function normalizeProperties(properties: TrackProperties = {}): Record<string, string | number | boolean> {
  const result: Record<string, string | number | boolean> = {};
  Object.entries(properties).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      result[key] = value;
      return;
    }
    result[key] = String(value);
  });
  return result;
}

/** Whether we should skip Mixpanel sends (policy on + current policy email matches internal patterns). */
function isMixpanelBlockedByInternalEmailPolicy(): boolean {
  if (!excludeInternalEmailsEnabled) return false;
  return shouldExcludeInternalEmailFromMixpanel(mixpanelPolicyEmail);
}

function syncInternalEmailExclusion(email: string | undefined): void {
  if (!initialized) return;
  const wasBlocked = isMixpanelBlockedByInternalEmailPolicy();
  if (!excludeInternalEmailsEnabled) {
    mixpanelPolicyEmail = undefined;
    if (wasBlocked) mixpanel.opt_in_tracking();
    return;
  }
  mixpanelPolicyEmail = email;
  const nowBlocked = shouldExcludeInternalEmailFromMixpanel(email);
  if (nowBlocked && !wasBlocked) mixpanel.opt_out_tracking();
  if (!nowBlocked && wasBlocked) mixpanel.opt_in_tracking();
}

export function initMixpanel(token?: string, options: MixpanelInitOptions = {}): void {
  const safeToken = String(token || "").trim();
  if (initialized || !safeToken) return;
  excludeInternalEmailsEnabled = Boolean(options.excludeInternalEmails);
  const recordSessionsPercent = clampRecordSessionsPercent(options.recordSessionsPercent ?? 0);
  const persistence = options.persistence ?? "localStorage";
  const autocapture = options.autocapture !== false;
  mixpanel.init(safeToken, {
    autocapture,
    track_pageview: false,
    // SDK typings only list "cookie" | "localStorage"; runtime also accepts "localStorage+cookie" and "memory".
    persistence: persistence as Persistence,
    ignore_dnt: false,
    record_sessions_percent: recordSessionsPercent,
  });
  const superProps = normalizeProperties(options.superProperties ?? {});
  if (Object.keys(superProps).length > 0) {
    mixpanel.register(superProps);
  }
  initialized = true;
}

/** Call when Auth0 user email may have changed — applies internal-email opt-out / opt-in. */
export function refreshMixpanelInternalEmailExclusion(userEmail: string | undefined): void {
  syncInternalEmailExclusion(userEmail);
}

export function identifyMixpanelUser(distinctId?: string, props: TrackProperties = {}): void {
  if (!initialized || !distinctId) return;
  const emailForPolicy = String(props.email ?? "").trim() || undefined;
  identifiedUserEmail = emailForPolicy;
  syncInternalEmailExclusion(emailForPolicy);
  if (isMixpanelBlockedByInternalEmailPolicy()) return;
  mixpanel.identify(distinctId);
  const { email: _email, ...rest } = props;
  const peopleProps = mixpanelEmailPeopleProperties(emailForPolicy, normalizeProperties(rest) as Record<string, string>);
  if (Object.keys(peopleProps).length > 0) {
    mixpanel.people.set(peopleProps);
  }
  const emailSuperProps = mixpanelEmailEventProperties(emailForPolicy);
  if (Object.keys(emailSuperProps).length > 0) {
    mixpanel.register(emailSuperProps);
  }
}

export function trackMixpanelEvent(eventName: string, properties: TrackProperties = {}): void {
  if (!initialized || !eventName) return;
  if (isMixpanelBlockedByInternalEmailPolicy()) return;
  const explicitEmail = String(properties.email ?? "").trim() || undefined;
  const emailProps = mixpanelEmailEventProperties(explicitEmail || identifiedUserEmail || mixpanelPolicyEmail);
  mixpanel.track(eventName, {
    ...emailProps,
    ...normalizeProperties(properties),
  });
}

export function trackMixpanelPageView(pathname: string, search = ""): void {
  if (!initialized || isMixpanelBlockedByInternalEmailPolicy()) return;
  const path = String(pathname || "/");
  const query = String(search || "");
  const pathQuery = `${path}${query}`;
  const pageFullUrl =
    typeof window !== "undefined" && window.location?.origin
      ? `${window.location.origin}${pathQuery}`
      : pathQuery;
  trackMixpanelEvent("page_view", {
    page_path: path,
    page_query: query,
    page_url: pathQuery,
    page_path_query: pathQuery,
    page_full_url: pageFullUrl,
    "$current_url": pageFullUrl,
  });
}

export function resetMixpanelUser(): void {
  if (!initialized) return;
  if (isMixpanelBlockedByInternalEmailPolicy()) {
    mixpanel.opt_in_tracking();
  }
  mixpanelPolicyEmail = undefined;
  identifiedUserEmail = undefined;
  mixpanel.reset();
}
